/**
 * NAVIGATION
 */

import { computed, signal } from "@angular/core";
import { AccessLevel, CommonBaseModel, FormPageType, TruthCondition } from "app/shared/model";

/** Represents a form navigation menu structure. */
export class FormNavigation implements IFormNavigation {
    /** Gets or sets The product ID. */
    formId!: string | undefined;
    /** Gets or sets the form version. */
    formVersion!: number;
    /** Gets or sets the product branch name. */
    productBranchName!: string | undefined;
    /** Gets or sets the navigation items list. */
    navigationItems!: FormNavigationItem[] | undefined;
    id!: string | undefined;

    constructor(data?: IFormNavigation) {
        if (data) {
            for (let property in data) {
                if (data.hasOwnProperty(property)) {
                    (<any>this)[property] = (<any>data)[property];
                }
            }
        }
    }

    init(_data?: any) {
        if (_data) {
            this.formId = _data["formId"];
            this.formVersion = _data["formVersion"];
            this.productBranchName = _data["productBranchName"];
            if (Array.isArray(_data["navigationItems"])) {
                this.navigationItems = [] as any;
                for (let item of _data["navigationItems"])
                    this.navigationItems!.push((new FormNavigationItem()).init(item));
            }
            this.id = _data["id"];
        }
    }

    static fromJS(data: any): FormNavigation {
        data = typeof data === 'object' ? data : {};
        let result = new FormNavigation();
        result.init(data);
        return result;
    }

    toJSON(data?: any) {
        data = typeof data === 'object' ? data : {};
        data["formId"] = this.formId;
        data["formVersion"] = this.formVersion;
        data["productBranchName"] = this.productBranchName;
        if (Array.isArray(this.navigationItems)) {
            data["navigationItems"] = [];
            for (let item of this.navigationItems)
                data["navigationItems"].push(item.toJSON());
        }
        data["id"] = this.id;
        return data;
    }
}

/** Represents a form navigation menu structure. */
export interface IFormNavigation {
    /** Gets or sets The product ID. */
    formId: string | undefined;
    /** Gets or sets the form version. */
    formVersion: number;
    /** Gets or sets the product branch name. */
    productBranchName: string | undefined;
    /** Gets or sets the navigation items list. */
    navigationItems: FormNavigationItem[] | undefined;
    id: string | undefined;
}

/** Represents a form navigation item. */
export class FormNavigationItem extends CommonBaseModel
{
    /** Gets or sets the page title. */
    title!: string | undefined;
    /** Gets or sets the one-based page identifier. */
    pageId!: number;
    /** Gets or sets the sub-page control ID. */
    controlId!: number | undefined;
    /** Gets or sets a value indicating whether the page submits the application. */
    submitsApplication!: boolean;
    visibilityRule: TruthCondition = <any>undefined;
    accessLevel!: AccessLevel;
    /** Gets or sets the sub navigation items. */
    subItems!: FormNavigationItem[] | undefined;
    pageType!: FormPageType;

    progress: FormNavigationProgress = new FormNavigationProgress(0);

    // CUSTOM
    pageTabIndex: number = 0; // Used to bind a navigation item to a page tab.
    sortIndex: number = 0; // Used to determine index without a function call!

    visible = signal(true);

    init(_data?: any): this {
        if (!_data || typeof _data !== 'object') {
            return this;
        }
        _data = _data["response"] ? _data["response"] : _data;

        super.init(_data);

        this.visibilityRule = _data["VisibilityRule"]
            ? (new TruthCondition()).init(_data["VisibilityRule"])
            : <any>undefined;

        // ToDo: Watch Visibility here? (Not an angular component... can't inject services)

        this.subItems = [];
        let subItems = _data["SubItems"];
        if (Array.isArray(subItems)) {
            for (let item of subItems) {
                this.subItems.push((new FormNavigationItem()).init(item));
            }
        }

        return this;
    }

    toJSON(data?: any) {
        data = typeof data === 'object' ? data : {};
        data["title"] = this.title;
        data["pageId"] = this.pageId;
        data["controlId"] = this.controlId;
        data["submitsApplication"] = this.submitsApplication;
        data["visibilityRule"] = this.visibilityRule ? this.visibilityRule.toJSON() : <any>undefined;
        data["accessLevel"] = this.accessLevel;
        if (Array.isArray(this.subItems)) {
            data["subItems"] = [];
            for (let item of this.subItems)
                data["subItems"].push(item.toJSON());
        }
        data["pageType"] = this.pageType;
        return data;
    }
}

export class FormNavigationProgress {
    stepCount: number = 0;
    currentStep: number = 0;

    percent = signal<number>(0);

    constructor(stepCount: number) {
        this.stepCount = stepCount < 0 ? 0 : stepCount;
        this.currentStep = 0;
    }

    setStepCount(stepCount: number) {
        this.stepCount = stepCount < 0 ? 0 : stepCount;
        this.setStep(this.currentStep);
    }

    setStep(currentStep: number) {
        this.currentStep = (currentStep < 0)
            ? 0
            : (currentStep > this.stepCount)
                ? this.stepCount
                : currentStep;

        if (this.stepCount == 0) {
            this.percent.set(0);
            return;
        }

        let percent = (this.currentStep / this.stepCount) * 100;
        let progress = Math.round(percent);
        this.percent.set(progress);
    }
}
